// One build serves every brand's dashboard. Which brand is decided at runtime from the hostname,
// not from REACT_APP_* env: CRA bakes env at build time, so env can't vary per host.
//
// A brand's race data and booking come on once its venue serves its own data (TRZ: on; GTSR: off). Race data comes
// from `raceDataApi`: CommonApi (World of Racing's databases) for WOR, the venue's own WebApi for
// TRZ/GTSR, which serves the same routes for its own drivers and links the dashboard account to its
// own driver row (`driver/me`, `driver/link`). Memberships, credits, race nights, Race Around the
// World, career and AI analysis stay World of Racing's (CommonApi / Identity wallet), so turning one
// on for another brand would show that brand's customers World of Racing data. Signup and email
// change come on once Identity sends that brand's own emails (TRZ: on; GTSR: off).

export type TenantKey = 'wor' | 'trz' | 'gtsr';

export interface TenantFeatures {
  /** Laps, leaderboards, telemetry: read from the tenant's `raceDataApi`. */
  raceData: boolean;
  /** The Career tab: CommonApi challenges, World of Racing only. */
  career: boolean;
  /** The telemetry page's AI race engineer: CommonApi analysis, World of Racing only. */
  aiAnalysis: boolean;
  /** The public, no-login lap view: CommonApi's anonymous endpoints, World of Racing laps only. */
  publicLap: boolean;
  /** Race Around the World. */
  ratw: boolean;
  /** Credits balance, Buy, /checkout and gifts: the Identity wallet. */
  credits: boolean;
  /** The plan card and membership links. */
  memberships: boolean;
  raceNights: boolean;
  /** Book a session (at `bookingVenues`) and the preferred-venue setting. */
  booking: boolean;
  signup: boolean;
  /** Identity sends the change link from World of Racing's dashboard and adds the address to its Drip list. */
  emailChange: boolean;
  /** Booking links admin and simulator assignments. */
  staffTools: boolean;
}

/** A venue a member can book; `key` is a `LOCATION_API_BASE` key. */
export interface BookingVenue {
  key: string;
  label: string;
}

export interface TenantBrand {
  name: string;
  /** Short form for tight labels ("WOR AI Engineer"). */
  shortName: string;
  logo: string;
  logoAlt: string;
  /** Multiplies LogoLink's height: a wide wordmark needs less height than WOR's stacked logo. */
  logoHeightScale: number;
  favicon: string;
  appleTouchIcon: string;
  description: string;
  themeColor: string;
  supportEmail: string;
  websiteUrl: string;
  gtmId?: string;
}

export interface TenantTheme {
  /** The brand's primary colour. Named `red` because World of Racing's is; TRZ's is gold. */
  red: string;
  lightRed: string;
  /** `red` as "r, g, b" for rgba(var(--red-rgb), a). */
  redRgb: string;
  /** Text and icons on a filled `red` surface: white on red, near-black on gold (white on gold is ~1.9:1). */
  onRed: string;
  /** The dark end of the account avatar's gradient. */
  redDeep: string;
  /** Errors and alerts. Stays red for every brand: red means something went wrong. */
  danger: string;
  dangerRgb: string;
  /** Error text on the alert's 10% `danger` tint: a lighter red, because `danger` itself is under 4.5:1 there. */
  dangerText: string;
  accent: string;
  accentRgb: string;
}

export interface Tenant {
  key: TenantKey;
  hosts: string[];
  /** Any host under this domain is the brand, so a host missing from `hosts` can't fall back to WOR. */
  domain?: string;
  brand: TenantBrand;
  theme: TenantTheme;
  features: TenantFeatures;
  /** Base URL of the API serving this brand's race data and driver link (`DriverClient`). */
  raceDataApi: string;
  /** The venues Book a session offers. A single venue is selected for the member. */
  bookingVenues: BookingVenue[];
}

const ALL_ON: TenantFeatures = {
  raceData: true,
  career: true,
  aiAnalysis: true,
  publicLap: true,
  ratw: true,
  credits: true,
  memberships: true,
  raceNights: true,
  booking: true,
  signup: true,
  emailChange: true,
  staffTools: true,
};

const ALL_OFF: TenantFeatures = {
  raceData: false,
  career: false,
  aiAnalysis: false,
  publicLap: false,
  ratw: false,
  credits: false,
  memberships: false,
  raceNights: false,
  booking: false,
  signup: false,
  emailChange: false,
  staffTools: false,
};

export const TENANTS: Record<TenantKey, Tenant> = {
  wor: {
    key: 'wor',
    hosts: ['dashboard.worldofracing.com'],
    brand: {
      name: 'World Of Racing',
      shortName: 'WOR',
      logo: '/logo.png',
      logoAlt: 'World Of Racing',
      logoHeightScale: 1,
      favicon: '/favicon.png',
      appleTouchIcon: '/logo.png',
      description: 'Racers Are Made Here',
      themeColor: '#000000',
      supportEmail: 'info@worldofracing.com',
      websiteUrl: 'https://www.worldofracing.com',
      gtmId: 'GTM-5BD7L9K8',
    },
    theme: {
      red: '#ed1c24', lightRed: '#d41920', redRgb: '237, 28, 36', onRed: '#ffffff', redDeep: '#7a0d12',
      danger: '#ed1c24', dangerRgb: '237, 28, 36', dangerText: '#ff5a52', accent: '#f8b833', accentRgb: '248, 184, 51',
    },
    features: ALL_ON,
    raceDataApi: process.env.REACT_APP_WORLD_API_BASE_URL ?? '',
    bookingVenues: [
      { key: 'pasadena', label: 'Pasadena' },
      { key: 'tustin', label: 'Tustin' },
      { key: 'kansascity', label: 'Kansas City' },
      { key: 'phoenix', label: 'Phoenix' },
    ],
  },
  trz: {
    key: 'trz',
    hosts: ['dashboard.theracingzone.com'],
    domain: 'theracingzone.com',
    brand: {
      name: 'The Racing Zone',
      shortName: 'TRZ',
      logo: '/brands/trz/logo.png',
      logoAlt: 'The Racing Zone',
      logoHeightScale: 0.42,
      favicon: '/brands/trz/favicon.png',
      appleTouchIcon: '/brands/trz/icon-192.png',
      description: 'Your Racing Zone driver dashboard',
      themeColor: '#000000',
      supportEmail: 'support@theracingzone.com',
      websiteUrl: 'https://theracingzone.com',
    },
    // Gold, matching TRZ's booking site, Race Control and order email. Two-tone: the accent is the
    // same gold. Text on a gold fill is near-black; errors stay red.
    theme: {
      red: '#faa700', lightRed: '#d68f00', redRgb: '250, 167, 0', onRed: '#1a1a1a', redDeep: '#b37700',
      danger: '#e0201c', dangerRgb: '224, 32, 28', dangerText: '#ff5a52', accent: '#faa700', accentRgb: '250, 167, 0',
    },
    // Laps, boards, telemetry and booking from vegas-api, which serves TRZ's own drivers (Phase B).
    // Signup and email change: Identity sends TRZ-branded emails (Phase A).
    features: { ...ALL_OFF, raceData: true, booking: true, signup: true, emailChange: true },
    raceDataApi: process.env.REACT_APP_VEGAS_API_BASE_URL ?? '',
    bookingVenues: [{ key: 'vegas', label: 'Las Vegas' }],
  },
  gtsr: {
    key: 'gtsr',
    hosts: ['dashboard.gtsimrace.com'],
    domain: 'gtsimrace.com',
    brand: {
      name: 'GT Sim Racing',
      shortName: 'GTSR',
      logo: '/brands/gtsr/logo.png',
      logoAlt: 'GT Sim Racing',
      logoHeightScale: 1,
      favicon: '/brands/gtsr/favicon.png',
      appleTouchIcon: '/brands/gtsr/icon-192.png',
      description: 'Your GT Sim Racing driver dashboard',
      themeColor: '#000000',
      supportEmail: 'support@gtsimrace.com',
      websiteUrl: 'https://gtsimrace.com',
    },
    // Red from the GTSR wordmark; the accent is the shield's Italian green, lightened to read on black.
    theme: {
      red: '#e01e26', lightRed: '#d21f25', redRgb: '224, 30, 38', onRed: '#ffffff', redDeep: '#7a0d12',
      danger: '#e01e26', dangerRgb: '224, 30, 38', dangerText: '#ff5a52', accent: '#34c06f', accentRgb: '52, 192, 111',
    },
    features: ALL_OFF,
    raceDataApi: process.env.REACT_APP_GTR_API_BASE_URL ?? '',
    bookingVenues: [],
  },
};

const DEV_HOSTS = new Set(['localhost', '127.0.0.1']);
const OVERRIDE_KEY = 'dashboard.tenant';

const isTenantKey = (v: string | null | undefined): v is TenantKey =>
  !!v && Object.prototype.hasOwnProperty.call(TENANTS, v);

const normalizeHost = (hostname: string) =>
  hostname.trim().toLowerCase().replace(/\.$/, '').replace(/^www\./, '');

/**
 * The brand for a hostname. An unknown host is World of Racing, so the azurewebsites hosts (QA and
 * the prod app's own name) keep working. On localhost only, `?tenant=trz|gtsr|wor` picks a brand and
 * is remembered for the tab, so a dev can click around one brand without re-adding it to every URL.
 */
export function resolveTenant(
  hostname: string,
  search = '',
  storage?: Pick<Storage, 'getItem' | 'setItem'>,
): Tenant {
  const host = normalizeHost(hostname);

  if (DEV_HOSTS.has(host)) {
    const requested = new URLSearchParams(search).get('tenant');
    if (isTenantKey(requested)) {
      try { storage?.setItem(OVERRIDE_KEY, requested); } catch { /* storage blocked: still honour it */ }
      return TENANTS[requested];
    }
    let remembered: string | null = null;
    try { remembered = storage?.getItem(OVERRIDE_KEY) ?? null; } catch { /* storage blocked */ }
    if (isTenantKey(remembered)) return TENANTS[remembered];
    return TENANTS.wor;
  }

  const all = Object.values(TENANTS);
  return all.find((t) => t.hosts.includes(host))
    ?? all.find((t) => t.domain && (host === t.domain || host.endsWith(`.${t.domain}`)))
    ?? TENANTS.wor;
}

const sessionStore = (): Storage | undefined => {
  try { return window.sessionStorage; } catch { return undefined; }
};

/** Resolved once at load: the hostname can't change without a reload. */
export const currentTenant: Tenant =
  typeof window === 'undefined'
    ? TENANTS.wor
    : resolveTenant(window.location.hostname, window.location.search, sessionStore());
